import { validateUpcLength } from './upcValidation';

function parseCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current);
  return result;
}

export function normalizeInventoryUpc(upc) {
  return String(upc || '').replace(/\D/g, '');
}

function readWithFileReader(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read CSV file.'));
    reader.readAsText(file);
  });
}

/**
 * Read a CSV file as text. Uses FileReader for broad browser compatibility.
 */
export function readCsvFileAsText(file) {
  if (!file) {
    return Promise.reject(new Error('No file selected.'));
  }

  if (typeof file.text === 'function') {
    return file.text().catch(() => readWithFileReader(file));
  }

  return readWithFileReader(file);
}

/**
 * Parse inventory bulk-update CSV with headers: upc, quantity, price
 */
export function parseInventoryCsv(text) {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) {
    return { rows: [], errors: ['CSV must include a header row and at least one data row.'] };
  }

  const headers = parseCsvLine(lines[0]).map((header) => header.trim().toLowerCase());
  const upcIndex = headers.indexOf('upc');
  const quantityIndex = headers.indexOf('quantity');
  const priceIndex = headers.indexOf('price');

  if (upcIndex === -1 || quantityIndex === -1 || priceIndex === -1) {
    return { rows: [], errors: ['CSV must include headers: upc, quantity, price'] };
  }

  const rows = [];
  const errors = [];

  for (let i = 1; i < lines.length; i += 1) {
    const cells = parseCsvLine(lines[i]);
    if (cells.every((cell) => !cell.trim())) continue;

    const upc = normalizeInventoryUpc(cells[upcIndex]);
    const quantityStr = (cells[quantityIndex] || '').trim();
    const priceStr = (cells[priceIndex] || '').trim();

    if (!upc) {
      errors.push(`Row ${i + 1}: missing or invalid UPC`);
      continue;
    }

    // Validate UPC length
    const upcValidation = validateUpcLength(upc);
    if (!upcValidation.valid) {
      errors.push(`Row ${i + 1}: ${upcValidation.error}`);
      continue;
    }

    const quantity = parseInt(quantityStr, 10);
    if (Number.isNaN(quantity) || quantity < 0) {
      errors.push(`Row ${i + 1}: invalid quantity "${quantityStr}"`);
      continue;
    }

    const price = parseFloat(priceStr);
    if (Number.isNaN(price) || price < 0) {
      errors.push(`Row ${i + 1}: invalid price "${priceStr}"`);
      continue;
    }

    rows.push({ upc, quantity, price, rowNumber: i + 1 });
  }

  return { rows, errors };
}

/**
 * Build a map of UPC -> row data. Later rows override earlier rows for duplicate UPCs.
 */
export function mapInventoryCsvRows(rows) {
  const byUpc = new Map();
  rows.forEach((row) => {
    byUpc.set(row.upc, row);
  });
  return byUpc;
}

/**
 * Build a UPC -> product map from product records.
 */
export function buildProductsByUpc(products) {
  const productsByUpc = new Map();
  products.forEach((product) => {
    const upc = normalizeInventoryUpc(product.upc);
    if (upc && !productsByUpc.has(upc)) {
      productsByUpc.set(upc, product);
    }
  });
  return productsByUpc;
}

/**
 * Match parsed CSV rows to inventory items and build an update/add preview.
 */
export function buildInventoryCsvPreview(rows, inventoryItems, { productsByUpc = new Map(), targetSupermarkets = [] } = {}) {
  const csvByUpc = mapInventoryCsvRows(rows);
  const updates = [];
  const additions = [];
  const notFoundUpcs = [];
  const pendingSupermarketUpcs = [];

  const findInventoryMatches = (upc, product) => {
    const matches = [];
    inventoryItems.forEach((item) => {
      const itemUpc = normalizeInventoryUpc(item.product_upc);
      const upcMatch = itemUpc === upc;
      const productMatch = product && item.product_id === product.id;
      if (upcMatch || productMatch) {
        matches.push(item);
      }
    });
    return matches;
  };

  csvByUpc.forEach((csvRow, upc) => {
    const product = productsByUpc.get(upc);
    const inventoryMatches = findInventoryMatches(upc, product);

    if (inventoryMatches.length > 0) {
      inventoryMatches.forEach((item) => {
        const currentQuantity = typeof item.quantity === 'number' ? item.quantity : Number(item.quantity) || 0;
        const currentPrice = typeof item.sale_price === 'number' ? item.sale_price : Number(item.sale_price) || 0;

        updates.push({
          id: item.id,
          upc,
          productName: item.product_name || item.product_id,
          supermarketName: item.supermarket_name || item.supermarket_id,
          currentQuantity,
          currentPrice,
          quantity: csvRow.quantity,
          sale_price: csvRow.price,
          action: 'update',
        });
      });
      return;
    }

    if (!product) {
      notFoundUpcs.push(upc);
      return;
    }

    if (targetSupermarkets.length === 0) {
      pendingSupermarketUpcs.push({
        upc,
        productName: product.display_name || product.id,
        quantity: csvRow.quantity,
        sale_price: csvRow.price,
      });
      return;
    }

    targetSupermarkets.forEach((supermarket) => {
      additions.push({
        productId: product.id,
        productName: product.display_name || product.id,
        productUpc: upc,
        supermarketId: supermarket.id,
        supermarketName: supermarket.display_name || supermarket.id,
        quantity: csvRow.quantity,
        sale_price: csvRow.price,
        action: 'add',
      });
    });
  });

  return {
    updates,
    additions,
    notFoundUpcs,
    pendingSupermarketUpcs,
    csvRowCount: rows.length,
  };
}
